import { useCallback, useMemo, useState, useTransition } from 'react';
import type { Selection } from 'react-aria-components';
import { useProjectContext } from '@gonfalon/app-context/useProjectContext';
import { enableViewCreationFromSelect, enableViews } from '@gonfalon/dogfood-flags/dogfood-flags';
import { ContextualFeedbackButton } from '@gonfalon/feedback/ContextualFeedbackButton';
import type { UIFlagFilter } from '@gonfalon/flag-filters/types';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { AccessCheck } from '@gonfalon/permissions/access-check';
import { useLatestRef } from '@gonfalon/react/useLatestRef';
import { getProjectQuery } from '@gonfalon/rest-api/queries/projects';
import { getViewsQuery } from '@gonfalon/rest-api/queries/views';
import { SearchField } from '@gonfalon/ui/Form';
import type { SectionedData } from '@gonfalon/ui/ResourceSelector';
import { ResourceSelector, SearchResults } from '@gonfalon/ui/ResourceSelector';
import { TextTruncator } from '@gonfalon/ui/TextTruncator';
import { Box } from '@launchpad-ui/box';
import {
  Button,
  ButtonGroup,
  IconButton,
  ListBoxItem,
  ProgressBar,
  snackbarQueue,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';
import { useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { uniqBy } from 'lodash';

import type { ViewSelectedResource } from '../../components/AddResourcesToViewModal';
import { fetchCanLinkResourceToView, useViewLinkAccessByKey } from '../../linkAccess';
import type { LinkableResourceType, View } from '../../types';
import { getHumanLinkableResourceType } from '../../utils';
import { useViewCreation } from '../../ViewCreationContext';
import { VIEWS_QUERY_STALE_TIME } from '../constants';

import { ProjectEmptyViewsState } from './ProjectEmptyViewsState';
import { useFavoriteViews } from './useFavoriteViews';
import { ViewDetailsPanel } from './ViewDetailsPanel';

import styles from './RichViewsDialog.module.css';

const RICH_SELECTOR_EXPANDED_CONTAINER_STYLE = {
  flex: '1 1 auto',
  flexGrow: 1,
  minHeight: 0,
  height: 'auto',
  width: '100%',
  gridTemplateColumns: '1fr 1fr',
} as const;

const RICH_SELECTOR_COLLAPSED_CONTAINER_STYLE = {
  flex: '1 1 auto',
  flexGrow: 1,
  minHeight: 0,
  height: 'auto',
  width: 'var(--lp-size-320)',
} as const;

const RICH_SELECTOR_HEADER_STYLE = {
  padding: 'var(--lp-spacing-300)',
  borderBottom: '1px solid var(--lp-color-border-ui-primary)',
} as const;

export type RichViewsDialogProps = {
  onSelectionChange: (views: View[]) => void;
  selectedViews: View[];
  label?: string;
  selectionMode?: 'single' | 'multiple';
  allowClear?: boolean;
  onBeforeCreateView?: () => void;
  onFeedbackPress?: () => void;
  onToggleDetails?: () => void;
  isExpanded?: boolean;
  hideSelectionFooter?: boolean;
  flagFilters?: UIFlagFilter;
  resourceType?: LinkableResourceType;
  initialSelectedResources?: Array<ViewSelectedResource<LinkableResourceType>>;
  hideCreateAndClose?: boolean;
  /**
   * When true, selecting/deselecting a view row links/unlinks a `resourceType`
   * resource on the backend (REL-15125), so rows the member may not
   * link/unlink render disabled with an explanation, keyed off each view's
   * `_access`. Leave unset in contexts where selection doesn't mutate view
   * links — filters, and creation-time association (which the backend
   * intentionally allows with only `viewView` access).
   */
  enforceLinkAccess?: boolean;
  /**
   * When true, suppress the header `Create` affordance. Useful in filter
   * contexts where inline view creation doesn't make sense (e.g. the SDK Keys
   * view filter).
   */
  hideCreateButton?: boolean;
  /**
   * Project key to scope the views query against. When omitted, falls back to the active
   * `useProjectContext()` — required for callers mounted under the `project-context` route loader.
   *
   * Callers mounted **outside** the project-context loader (e.g. payload-filtering-v2's
   * `NewSdkKeyModal` at `/settings/sdk-keys`) MUST pass this explicitly.
   */
  projectKey?: string;
};

export function RichViewsDialog({ projectKey: projectKeyProp, ...props }: RichViewsDialogProps) {
  // Fall back to the active project context when the caller doesn't supply a projectKey. The
  // `optional: true` flag prevents the invariant from firing when this dialog is mounted under a
  // route without the project-context loader (e.g. `/settings/sdk-keys`); in that case the caller
  // MUST pass `projectKey` explicitly — we throw if neither source resolves.
  const projectContext = useProjectContext({ optional: true });
  const projectKey = projectKeyProp ?? projectContext?.project.key;
  // Resolve `projectKey` and guard here — before any other hooks run — so the conditional throw can
  // never change the hook count across renders. The hook-heavy body lives in `RichViewsDialogContent`,
  // which only mounts once we have a guaranteed `projectKey`.
  if (!projectKey) {
    throw new Error(
      'RichViewsDialog requires a `projectKey` prop when rendered outside of the project-context route loader.',
    );
  }
  // Only forward the context project when the caller didn't override `projectKey`. Otherwise the
  // content component would prefer the context's project over the one fetched for the explicit
  // `projectKeyProp`, handing a mismatched project to downstream `AccessCheck` wrappers.
  return (
    <RichViewsDialogContent
      {...props}
      projectKey={projectKey}
      contextProject={projectKeyProp ? undefined : projectContext?.project}
    />
  );
}

type RichViewsDialogContentProps = Omit<RichViewsDialogProps, 'projectKey'> & {
  projectKey: string;
  contextProject?: NonNullable<ReturnType<typeof useProjectContext>>['project'];
};

function RichViewsDialogContent({
  onSelectionChange,
  selectedViews = [],
  label = 'Select views',
  selectionMode = 'single',
  allowClear,
  onBeforeCreateView,
  onFeedbackPress,
  onToggleDetails,
  isExpanded = true,
  hideSelectionFooter = false,
  flagFilters,
  resourceType,
  initialSelectedResources,
  hideCreateAndClose = false,
  enforceLinkAccess = false,
  hideCreateButton = false,
  projectKey,
  contextProject,
}: RichViewsDialogContentProps) {
  const [filterText, setFilterText] = useState('');
  const [isTransitioning, startTransition] = useTransition();
  const queryClient = useQueryClient();
  // `project` is required by downstream `AccessCheck` wrappers. Inside the project-context loader
  // we already have it from `contextProject`; outside (e.g. `NewSdkKeyModal` at `/settings/sdk-keys`)
  // we suspense-fetch from the resolved `projectKey`. The query is cached by React Query, so when
  // the project context already populated it (in-loader path) this is effectively free.
  const { data: fetchedProject } = useSuspenseQuery(getProjectQuery({ projectKey }));
  const project = contextProject ?? fetchedProject;

  const { openCreateModal, setOnViewCreated } = useViewCreation();
  const isCreateViewFeatureEnabled = enableViews() && enableViewCreationFromSelect() && !hideCreateButton;
  const canRenderFeedback = Boolean(onFeedbackPress);

  const selectedViewsRef = useLatestRef(selectedViews);
  const onSelectionChangeRef = useLatestRef(onSelectionChange);

  const handleCreateClick = useCallback(() => {
    onBeforeCreateView?.();
    setOnViewCreated((view) => {
      // REL-15125: in linking contexts, auto-selecting the just-created view
      // commits a real link, and a member can hold `createView` without the
      // link action. Verify against the access-check endpoint and skip the
      // auto-link (the view itself was still created) with an explanation,
      // instead of letting the commit 403.
      if (enforceLinkAccess && resourceType !== undefined) {
        void fetchCanLinkResourceToView(queryClient, { project, resourceType, view }).then(
          ({ isAllowed, failedToCheck }) => {
            if (!isAllowed) {
              const noun = getHumanLinkableResourceType(resourceType).lower;
              snackbarQueue.add({
                description: failedToCheck
                  ? `View created, but we couldn't verify your permissions to link ${noun}s to it`
                  : `View created, but your permissions don't allow you to link ${noun}s to it`,
              });
              return;
            }
            onSelectionChangeRef.current([...selectedViewsRef.current, view]);
          },
        );
        return;
      }
      onSelectionChangeRef.current([...selectedViewsRef.current, view]);
    });
    if (hideCreateAndClose) {
      openCreateModal(filterText, flagFilters, resourceType, initialSelectedResources, { hideCreateAndClose });
    } else {
      openCreateModal(filterText, flagFilters, resourceType, initialSelectedResources);
    }
  }, [
    filterText,
    flagFilters,
    resourceType,
    initialSelectedResources,
    hideCreateAndClose,
    enforceLinkAccess,
    queryClient,
    project,
    onBeforeCreateView,
    openCreateModal,
    setOnViewCreated,
  ]);

  const { data: viewsRes, isPending } = useSuspenseQuery({
    ...getViewsQuery({
      projectKey,
      query: filterText ? { filter: { query: filterText } } : undefined,
      header: { 'LD-API-Version': 'beta' },
    }),
    staleTime: VIEWS_QUERY_STALE_TIME,
  });

  // Views selected when the dialog opened sort first; the order is a mount-time snapshot so that
  // selecting an item never reorders the open list under keyboard/screen-reader users (REL-15082).
  // The dialog remounts on each popover open, refreshing the snapshot. Live `selectedViews` are
  // still merged in (at the end) so every selected key resolves in `handleSelectionChange` and
  // stays removable even when the current search no longer returns it.
  const [viewsSelectedOnOpen] = useState(selectedViews);
  const allViews = useMemo(
    () => uniqBy([...viewsSelectedOnOpen, ...(viewsRes?.items ?? []), ...selectedViews], 'key'),
    [viewsRes, selectedViews, viewsSelectedOnOpen],
  );

  const isFiltered = filterText.trim().length > 0;
  const {
    canToggleFavorites,
    shouldRenderSections,
    favoriteViewKeys,
    orderedFavoriteViews,
    orderedAllNonFavoriteViews,
    handleFavoriteToggle,
  } = useFavoriteViews({ projectKey, allViews, selectedViews, selectionMode, isFiltered });

  const selectedKeys = useMemo(() => new Set(selectedViews.map((view) => view.key)), [selectedViews]);
  // REL-15125: per-row link/unlink access via the access-check endpoint
  // (batched + cached). Selected rows check the unlink action, unselected rows
  // the link action; rows stay locked while their check is in flight.
  const getViewLinkAccess = useViewLinkAccessByKey({
    isEnabled: enforceLinkAccess,
    project,
    resourceType,
    views: allViews,
    selectedViewKeys: selectedKeys,
  });
  const isLoading = isTransitioning || isPending;
  const toggleDetailsLabel = isExpanded ? 'Collapse details' : 'Expand details';
  const toggleDetailsIcon = isExpanded ? 'sidebar-right-collapse' : 'sidebar-right-expand';
  const containerStyle = isExpanded ? RICH_SELECTOR_EXPANDED_CONTAINER_STYLE : RICH_SELECTOR_COLLAPSED_CONTAINER_STYLE;

  const handleFilterChange = (value: string) => {
    startTransition(() => {
      setFilterText(value);
    });
  };

  const handleSelectionChange = useCallback(
    (selection: Selection) => {
      if (typeof selection === 'string') {
        const selectedView = allViews.find((view) => view.key === selection);
        onSelectionChange(selectedView ? [selectedView] : []);
        return;
      }

      const keys = Array.from(selection);
      if (keys.length > 0) {
        const views = keys.map((key) => allViews.find((v) => v.key === key)).filter((v) => !!v);
        onSelectionChange(views);
        return;
      }

      onSelectionChange([]);
    },
    [allViews, onSelectionChange],
  );

  const viewsData: SectionedData<View> | View[] = shouldRenderSections
    ? [
        { name: 'Favorites', items: orderedFavoriteViews },
        ...(orderedAllNonFavoriteViews.length > 0 ? [{ name: '', items: orderedAllNonFavoriteViews }] : []),
      ]
    : allViews;

  const renderDetails = useCallback(
    (view: View | null) => {
      if (!view) {
        return (
          <Box display="flex" justifyContent="center" alignItems="center" height="full">
            <ProgressBar size="small" isIndeterminate />
          </Box>
        );
      }

      return (
        <ViewDetailsPanel
          key={view.key}
          view={view}
          isFavorite={favoriteViewKeys.has(view.key)}
          onToggleFavorite={() => handleFavoriteToggle(view.key)}
          canToggleFavorites={canToggleFavorites}
          projectKey={projectKey}
        />
      );
    },
    [favoriteViewKeys, handleFavoriteToggle, canToggleFavorites, projectKey],
  );

  const isProjectEmpty = !isLoading && filterText.trim() === '' && allViews.length === 0;
  // Suppress the header Create CTA when `ProjectEmptyViewsState` renders — the
  // empty state already owns the primary CTA, so avoid a duplicate.
  const shouldRenderHeaderCreate = isCreateViewFeatureEnabled && !isProjectEmpty;

  const renderEmptyState = useCallback(() => {
    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" padding="$600">
          <ProgressBar aria-label={msg('views.status.loadingDots')} isIndeterminate size="small" />
        </Box>
      );
    }
    if (filterText.trim() !== '') {
      // Search no-match — keep existing behavior.
      return (
        <Box display="flex" justifyContent="center" alignItems="center" padding="$600">
          <>
            <Message id="views.viewsDialog.noViewsFound" />
          </>
        </Box>
      );
    }
    // Project has zero views — centered block with Create CTA.
    // Mirror the header gating: suppress the empty-state Create button whenever
    // the create-view feature itself is disabled (feature flags off, or caller
    // passed `hideCreateButton`). Otherwise we'd render a Create CTA here that
    // the rest of the dialog intentionally hides.
    return <ProjectEmptyViewsState onCreateView={handleCreateClick} hideCreateButton={!isCreateViewFeatureEnabled} />;
  }, [isLoading, filterText, handleCreateClick, isCreateViewFeatureEnabled]);

  const hasFooter = allowClear || canRenderFeedback;

  return (
    <div className={styles.richViewsContainer}>
      <ResourceSelector<View>
        selectedKeys={selectedKeys}
        onSelectionChange={handleSelectionChange}
        selectionMode={selectionMode}
        renderDetails={isExpanded ? renderDetails : undefined}
        renderEmptyState={renderEmptyState}
        containerStyle={containerStyle}
        headerStyle={RICH_SELECTOR_HEADER_STYLE}
        hideFooter={hideSelectionFooter}
        aria-label={label}
      >
        <div slot="header">
          <SearchField
            autoFocus
            className={styles.searchField}
            aria-label={msg('views.viewsDialog.searchLabel')}
            isBusy={isTransitioning}
            onChange={handleFilterChange}
            placeholder={isExpanded ? 'Search views...' : 'Search...'}
          />
          {(shouldRenderHeaderCreate || onToggleDetails) && (
            <>
              {isExpanded ? (
                <div className={styles.headerActionGroupExpanded}>
                  {shouldRenderHeaderCreate && (
                    <AccessCheck resourceType="view" action="createView" project={project} hideControlIfNotAllowed>
                      <Button variant="default" size="medium" onPress={handleCreateClick}>
                        <Icon name="add" size="small" /> {msg('views.viewsDialog.create')}
                      </Button>
                    </AccessCheck>
                  )}
                  {onToggleDetails && (
                    <TooltipTrigger>
                      <IconButton
                        aria-label={toggleDetailsLabel}
                        icon={toggleDetailsIcon}
                        size="medium"
                        variant="default"
                        className={styles.detailsToggleButton}
                        onPress={onToggleDetails}
                      />
                      <Tooltip>{toggleDetailsLabel}</Tooltip>
                    </TooltipTrigger>
                  )}
                </div>
              ) : (
                <ButtonGroup spacing="compact" className={styles.headerActionGroupCollapsed}>
                  {shouldRenderHeaderCreate && (
                    <AccessCheck resourceType="view" action="createView" project={project} hideControlIfNotAllowed>
                      <TooltipTrigger>
                        <IconButton
                          aria-label={msg('views.viewsDialog.createView')}
                          icon="add"
                          size="medium"
                          variant="default"
                          onPress={handleCreateClick}
                        />
                        <Tooltip>
                          <Message id="views.viewsDialog.createView" />
                        </Tooltip>
                      </TooltipTrigger>
                    </AccessCheck>
                  )}
                  {onToggleDetails && (
                    <TooltipTrigger>
                      <IconButton
                        aria-label={toggleDetailsLabel}
                        icon={toggleDetailsIcon}
                        size="medium"
                        variant="default"
                        className={styles.detailsToggleButton}
                        onPress={onToggleDetails}
                      />
                      <Tooltip>{toggleDetailsLabel}</Tooltip>
                    </TooltipTrigger>
                  )}
                </ButtonGroup>
              )}
            </>
          )}
        </div>
        <SearchResults<View>
          items={viewsData}
          getItemKey={(view) => view.key}
          // React Aria caches rendered rows by item identity; without this,
          // rows would stay frozen at their first-render (locked-while-loading)
          // access state once the async checks resolve. Matches the legacy
          // Menu's `dependencies`.
          dependencies={[getViewLinkAccess]}
          isLoading={isLoading && allViews.length === 0}
          resultsAreaClassName={styles.richResultsArea}
          aria-label={msg('views.viewsDialog.viewsLabel')}
        >
          {(view) => {
            // REL-15125: in linking contexts, selecting a row links a resource
            // to the view and deselecting unlinks it — both enforced per view
            // on the backend. Disable rows whose (un)link the member's role
            // denies, using the direction the toggle would fire; the
            // explanation is held back until a real denial resolves.
            const rowLinkAccess = getViewLinkAccess(view.key);
            return (
              <ListBoxItem key={view.key} id={view.key} textValue={view.name} isDisabled={!rowLinkAccess.isAllowed}>
                <div className={styles.viewListItem}>
                  <Icon name="stack" size="small" className={styles.viewNameIcon} />
                  <TextTruncator>{view.name}</TextTruncator>
                </div>
                {rowLinkAccess.lockedReason !== undefined && (
                  <Text slot="description">{rowLinkAccess.lockedReason}</Text>
                )}
              </ListBoxItem>
            );
          }}
        </SearchResults>
      </ResourceSelector>

      {hasFooter && (
        <div className={styles.richFooter}>
          <div className={styles.richFooterLeft}>
            {canRenderFeedback && <ContextualFeedbackButton size="small" variant="minimal" onPress={onFeedbackPress} />}
          </div>
          <div className={styles.richFooterRight}>
            {allowClear && selectedViews.length > 0 && (
              <Button size="small" variant="default" onPress={() => onSelectionChange([])}>
                <Icon name="cancel" size="small" /> {msg('views.viewsDialog.clearSelection')}
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
